import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Users, Award, Copy, CheckCircle, Share2, MessageCircle, Link as LinkIcon, Landmark, Plus, Loader2, ShieldCheck, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Base URL for referral shares — prefer production if user is on preview
const SHARE_ORIGIN = (() => {
  try {
    const h = window.location.hostname || "";
    if (h.includes("umaang.co")) return `https://${h}`;
    // For preview / localhost, fall back to production umaang.co (nicer share links)
    return "https://www.umaang.co";
  } catch { return "https://www.umaang.co"; }
})();

export default function Referrals() {
  const { user, refresh } = useAuth();
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const shareLink = d ? `${SHARE_ORIGIN}/auth?mode=register&ref=${encodeURIComponent(d.referral_code)}` : "";
  const shareMessage = d
    ? `${user?.name || "A friend"} ne aapko Umaang matrimony par invite kiya hai! 💐\n\nEk safe & verified matrimony experience join karein.\n\nRegister karein is link se:\n${shareLink}\n\nReferral code: ${d.referral_code}`
    : "";

  const load = async () => {
    try {
      const r = await api.get("/referrals/status");
      setD(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); }, []);

  const copy = () => {
    navigator.clipboard.writeText(d.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on Umaang Matrimony",
          text: shareMessage,
          url: shareLink,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const claim = async () => {
    try {
      const r = await api.post("/referrals/claim");
      toast.success(`Claimed! ${r.data.months_added} premium month(s) added.`);
      await refresh();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!d) return <div className="max-w-4xl mx-auto p-10 text-[color:var(--umg-text-2)]">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">Refer & Earn</div>
      <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
        <span className="bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent">रेफर करें अनलिमिटेड</span>
        <br />
        और कमाएँ भी <span className="bg-gradient-to-r from-[#059669] to-[#0EA5E9] bg-clip-text text-transparent">अनलिमिटेड</span>
      </h1>
      <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F59E0B] via-[#DB2777] to-[#7C3AED] text-white text-sm font-semibold shadow-md shadow-[#DB2777]/30">
        <Gift className="w-4 h-4" fill="white" /> 50% Earning on every plan
      </div>
      <p className="text-[color:var(--umg-text-2)] mt-4 max-w-2xl leading-relaxed">
        Apna referral link doston ke saath share karein. Jab bhi koi aapke link se Umaang par sign-up karke Premium plan (Silver / Gold / Gold Plus / Diamond) khareedta hai, aapko us purchase ka <b className="text-[#059669]">50% earning</b> milta hai. Koi limit nahi — jitne friends refer karein, utna kamayein.
      </p>

      <div className="mt-10 p-8 border border-[color:var(--umg-border)] relative overflow-hidden"
           style={{ borderRadius: 12, background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 40%, #FCA5A5 100%)" }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/30 blur-2xl" />
        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7C2D12]/70">Your code</div>
            <div className="font-display text-4xl mt-1 text-[#7C2D12]">{d.referral_code}</div>
          </div>
          <Button data-testid="copy-referral" onClick={copy} className="btn-pill bg-[#7C2D12] hover:bg-[#5A1F0D] text-white">
            {copied ? <><CheckCircle className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy code</>}
          </Button>
        </div>
      </div>

      {/* Shareable link + share buttons */}
      <div className="mt-4 p-6 border" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg grid place-items-center bg-[#7C3AED18] text-[#7C3AED]">
            <LinkIcon className="w-4 h-4" strokeWidth={2} />
          </div>
          <div>
            <div className="text-sm font-semibold">Your invite link</div>
            <div className="text-[11px] text-[color:var(--umg-text-2)]">Anyone who signs up through this link is auto-linked to you.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] p-2.5 rounded-lg border border-[color:var(--umg-border)] bg-[color:var(--umg-bg)] font-mono text-xs break-all select-all" data-testid="share-link">
            {shareLink}
          </div>
          <Button data-testid="copy-link-btn" onClick={copyLink} className="btn-pill bg-[#1C1917] hover:bg-black text-white h-10 px-4">
            {linkCopied ? <><CheckCircle className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy link</>}
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="share-whatsapp-btn" onClick={shareWhatsApp} className="btn-pill bg-[#25D366] hover:bg-[#1FB855] text-white h-10 px-4">
            <MessageCircle className="w-4 h-4 mr-1.5" fill="white" /> WhatsApp
          </Button>
          <Button data-testid="share-native-btn" onClick={nativeShare} className="btn-pill bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-10 px-4">
            <Share2 className="w-4 h-4 mr-1.5" /> Share via…
          </Button>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        {[
          { icon: Users, label: "Signups", value: d.total_referred, color: "#2563EB" },
          { icon: Award, label: "Verified", value: d.verified_referred, color: "#059669" },
          { icon: Gift,  label: "Free months earned", value: d.rewards_earned, color: "#B45309" },
        ].map((s) => (
          <div key={s.label} className="p-6 border relative overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-15" style={{ background: s.color }} />
            <div className="relative">
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${s.color}18`, color: s.color }}>
                <s.icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="font-display text-3xl mt-3">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--umg-text-2)] mt-1">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {d.claimable > 0 && (
        <div className="mt-6 p-6 bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white flex items-center justify-between shadow-lg shadow-[#C85A53]/30" style={{ borderRadius: 12 }}>
          <div>
            <div className="font-display text-2xl">{d.claimable} month{d.claimable > 1 ? "s" : ""} ready to claim</div>
            <div className="text-sm opacity-90 mt-1">Convert your referrals into premium time.</div>
          </div>
          <Button data-testid="claim-reward-btn" onClick={claim} className="btn-pill bg-white text-[#C85A53] hover:bg-white/90 font-semibold">
            Claim now
          </Button>
        </div>
      )}

      <div className="mt-8 text-sm text-[color:var(--umg-text-2)]">
        Progress to next reward: <b>{d.verified_referred % 3}/3</b> verified referrals.
      </div>

      {/* Bank Account for Payouts */}
      <PayoutSection />
    </div>
  );
}

// ---------------- Bank Account for Referral Payouts ----------------
function maskAccount(a) {
  if (!a) return "";
  if (a.length <= 4) return a;
  return "•".repeat(Math.max(4, a.length - 4)) + a.slice(-4);
}

function PayoutSection() {
  const [current, setCurrent] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: b }] = await Promise.all([
        api.get("/referrals/payout-details"),
        api.get("/referrals/banks"),
      ]);
      setCurrent(p.payout_account || null);
      setBanks(b.banks || []);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const hasAccount = current && Object.keys(current).some((k) => k !== "updated_at" && current[k]);

  return (
    <div className="mt-10 border rounded-2xl p-5 sm:p-6" style={{ borderColor: "var(--umg-border)", background: "var(--umg-surface)" }} data-testid="payout-section">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#DCFCE7] to-[#A7F3D0] text-[#047857] shrink-0">
          <Landmark className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <h3 className="font-display text-2xl">Bank Account for Payouts</h3>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#DCFCE7] text-[#047857] px-2 py-0.5 rounded-full">
              <ShieldCheck className="w-3 h-3" /> Encrypted
            </span>
          </div>
          <p className="text-xs text-[color:var(--umg-text-2)] mt-1">Add your bank account or UPI ID so Umaang can send your referral cash rewards directly. We only use it for verified payouts.</p>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 text-center py-10 text-[color:var(--umg-text-2)] text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>
      ) : hasAccount ? (
        <div className="mt-5 space-y-3">
          <div className="rounded-xl border p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" style={{ borderColor: "#E5E7EB", background: "#F9FAFB" }}>
            <PayoutRow label="Account holder" value={current.account_holder} />
            <PayoutRow label="Bank" value={current.bank_name} />
            <PayoutRow label="Account number" value={maskAccount(current.account_number)} mono />
            <PayoutRow label="IFSC" value={current.ifsc} mono />
            <PayoutRow label="Branch" value={current.branch} />
            <PayoutRow label="UPI ID" value={current.upi_id} mono />
          </div>
          <div className="flex justify-end">
            <Button data-testid="payout-edit-btn" onClick={() => setDialogOpen(true)} variant="outline" className="btn-pill">
              <Pencil className="w-3.5 h-3.5 mr-2" /> Update details
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-6 border-2 border-dashed rounded-xl p-6 text-center" style={{ borderColor: "#D1FAE5", background: "#F0FDF4" }}>
          <p className="text-sm text-[#065F46] mb-4">No payout account added yet — add one so your referral earnings can be transferred instantly.</p>
          <Button data-testid="payout-add-btn" onClick={() => setDialogOpen(true)} className="btn-pill bg-gradient-to-r from-[#059669] to-[#047857] hover:opacity-90 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add account
          </Button>
        </div>
      )}

      <PayoutDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        banks={banks}
        initial={current || {}}
        onSaved={(newAccount) => { setCurrent(newAccount); setDialogOpen(false); }}
      />
    </div>
  );
}

function PayoutRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <span className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] shrink-0 w-28 pt-0.5">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} ${value ? "" : "text-[color:var(--umg-text-2)] italic"}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function PayoutDialog({ open, onOpenChange, banks, initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [ifscLookup, setIfscLookup] = useState({ loading: false, result: null, error: null });
  const [saving, setSaving] = useState(false);
  const ifscTimerRef = React.useRef(null);
  const ifscReqIdRef = React.useRef(0);

  // Only reset form when the dialog transitions from closed → open. Ignore parent-render churn.
  useEffect(() => {
    if (open) {
      setForm(initial);
      setIfscLookup({ loading: false, result: null, error: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Manual IFSC lookup — called from onChange handler directly so React re-renders
  // and strict-mode double-invoke cannot cancel it.
  const doIfscLookup = (rawCode) => {
    const code = (rawCode || "").trim().toUpperCase();
    if (ifscTimerRef.current) clearTimeout(ifscTimerRef.current);
    if (!code || code.length < 11) {
      setIfscLookup({ loading: false, result: null, error: null });
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) {
      setIfscLookup({ loading: false, result: null, error: "Invalid IFSC format (e.g. HDFC0000123)" });
      return;
    }
    const reqId = ++ifscReqIdRef.current;
    setIfscLookup({ loading: true, result: null, error: null });
    ifscTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/referrals/ifsc/${code}`);
        if (reqId !== ifscReqIdRef.current) return;
        setIfscLookup({ loading: false, result: data, error: null });
        setForm((f) => {
          const upd = { ...(f || {}) };
          if (!upd.bank_name && data.bank) {
            const match = banks.find((b) => b.toLowerCase() === (data.bank || "").toLowerCase() || (data.bank || "").toLowerCase().includes(b.toLowerCase()));
            upd.bank_name = match || data.bank;
          }
          upd.branch = `${data.branch || ""}${data.city ? ", " + data.city : ""}`.trim();
          return upd;
        });
      } catch (e) {
        if (reqId !== ifscReqIdRef.current) return;
        setIfscLookup({ loading: false, result: null, error: formatError(e) });
      }
    }, 500);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/referrals/payout-details", {
        account_holder: form?.account_holder || "",
        bank_name: form?.bank_name || "",
        account_number: form?.account_number || "",
        ifsc: (form?.ifsc || "").toUpperCase(),
        upi_id: form?.upi_id || "",
        branch: form?.branch || "",
      });
      onSaved(data.payout_account);
      toast.success("Payout details saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...(form || {}), [k]: e.target?.value ?? e });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="payout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add / Update payout account</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs text-[color:var(--umg-text-2)]">Account holder name</label>
            <Input data-testid="payout-holder" placeholder="As on your passbook" value={form?.account_holder || ""} onChange={set("account_holder")} />
          </div>
          <div>
            <label className="text-xs text-[color:var(--umg-text-2)]">Bank name</label>
            <Select value={form?.bank_name || ""} onValueChange={(v) => setForm({ ...(form || {}), bank_name: v })}>
              <SelectTrigger data-testid="payout-bank-select">
                <SelectValue placeholder="Choose your bank" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {banks.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[color:var(--umg-text-2)]">Account number</label>
              <Input data-testid="payout-account" inputMode="numeric" placeholder="6–20 digits" value={form?.account_number || ""} onChange={set("account_number")} />
            </div>
            <div>
              <label className="text-xs text-[color:var(--umg-text-2)]">IFSC code</label>
              <div className="relative">
                <Input
                  data-testid="payout-ifsc"
                  placeholder="HDFC0000123"
                  value={(form?.ifsc || "").toUpperCase()}
                  maxLength={11}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setForm({ ...(form || {}), ifsc: v });
                    doIfscLookup(v);
                  }}
                />
                {ifscLookup.loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--umg-text-2)]" />}
              </div>
              {ifscLookup.result && (
                <div data-testid="payout-branch-hint" className="mt-1.5 text-[11px] text-[#047857] bg-[#DCFCE7] px-2 py-1 rounded-md flex items-start gap-1.5">
                  <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span><b>{ifscLookup.result.bank}</b> · {ifscLookup.result.branch}, {ifscLookup.result.city}</span>
                </div>
              )}
              {ifscLookup.error && (
                <div className="mt-1.5 text-[11px] text-[#B91C1C]">{ifscLookup.error}</div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-[color:var(--umg-text-2)]">UPI ID (optional)</label>
            <Input data-testid="payout-upi" placeholder="yourname@paytm  or  9876543210@ybl" value={form?.upi_id || ""} onChange={set("upi_id")} />
            <p className="text-[10px] text-[color:var(--umg-text-2)] mt-1">UPI is instant — bank transfer takes 1–2 working days.</p>
          </div>
          <div className="p-3 rounded-lg text-[11px] flex items-start gap-2" style={{ background: "#FEF3C7", color: "#78350F" }}>
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Payouts happen only after 3 verified referrals. Wrong details = wasted / failed transfer — please double-check.</span>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="payout-save-btn" onClick={submit} disabled={saving} className="bg-gradient-to-r from-[#059669] to-[#047857] hover:opacity-90 text-white">
            {saving ? "Saving…" : "Save details"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
